// @author Jayden Scahill - Old Mate Developments

import { create } from 'zustand';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { User, UserRole } from '../types';

interface AuthState {
  user: User | null;
  session: any;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  updateUserRole: (role: UserRole) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: true,

  signIn: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      // Fetch user role and details with increased timeout
      const userData = await handleSupabaseError(
        supabase
          .from('users')
          .select('id, email, name, role, created_at, updated_at')
          .eq('id', data.user.id)
          .single(),
        { timeout: 360000, retries: 5 }
      );

      set({
        user: {
          ...data.user,
          ...userData,
          role: userData?.role || 'readonly',
        },
        session: data.session,
      });
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw error;
    }
  },

  signUp: async (email: string, password: string, name: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name },
          emailRedirectTo: window.location.origin,
        }
      });
      if (error) throw error;

      if (data.user) {
        // Create user profile directly in the users table
        try {
          const userData = await handleSupabaseError(
            supabase
              .from('users')
              .insert({
                id: data.user.id,
                email: data.user.email,
                name: name,
                role: 'readonly'
              })
              .select('id, email, name, role, created_at, updated_at')
              .single(),
            { timeout: 360000, retries: 5 }
          );

          set({
            user: {
              ...data.user,
              ...userData,
              role: userData?.role || 'readonly',
            },
            session: data.session,
          });
        } catch (profileError: any) {
          // If user profile creation fails, still set the auth user
          console.warn('User profile creation failed, using auth user data:', profileError);
          set({
            user: {
              ...data.user,
              name: name,
              role: 'readonly',
            },
            session: data.session,
          });
        }
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw error;
    }
  },

  signOut: async () => {
    try {
      await handleSupabaseError(
        supabase.auth.signOut(),
        { timeout: 30000, retries: 3 }
      );
    } catch (error: any) {
      // Handle specific auth errors gracefully
      if (error.message === 'Auth session missing!') {
        console.warn('No active session to sign out from');
      } else if (error.message && error.message.includes('Connection lost')) {
        console.warn('Network connection lost during logout, proceeding with local cleanup');
      } else {
        console.error('Sign out error:', error);
        // Don't re-throw network-related errors to allow local cleanup to proceed
        if (!error.message.includes('Connection lost') && !error.message.includes('network')) {
          throw error;
        }
      }
    } finally {
      // Always clear local state and storage regardless of Supabase signOut result
      try {
        // Clear ALL auth data
        localStorage.removeItem('omtm-auth');
        localStorage.removeItem('sb-' + import.meta.env.VITE_SUPABASE_URL + '-auth-token');
        
        // Clear any other auth-related storage
        const authKeys = Object.keys(localStorage).filter(key => 
          key.startsWith('sb-') || 
          key.includes('auth') || 
          key.includes('supabase')
        );
        
        authKeys.forEach(key => localStorage.removeItem(key));
      } catch (storageError) {
        console.warn('Error clearing local storage:', storageError);
      }
      
      // Always clear the auth state
      set({ user: null, session: null });
    }
  },

  initializeAuth: async () => {
    try {
      // First check for an existing session with timeout
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        // Handle refresh token errors by signing out
        if (sessionError.message?.includes('refresh_token_not_found') || 
            sessionError.message?.includes('Invalid Refresh Token')) {
          await supabase.auth.signOut();
          set({ user: null, session: null, loading: false });
          return;
        }
        throw sessionError;
      }

      if (session?.user) {
        try {
          // Fetch user data with increased timeout and optimized query
          const userData = await handleSupabaseError(
            supabase
              .from('users')
              .select('id, email, name, role, created_at, updated_at')
              .eq('id', session.user.id)
              .single(),
            { timeout: 360000, retries: 5 }
          );

          set({
            user: {
              ...session.user,
              ...userData,
              role: userData?.role || 'readonly',
            },
            session,
            loading: false,
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
          set({
            user: {
              ...session.user,
              role: 'readonly',
            },
            session,
            loading: false,
          });
        }
      } else {
        set({ loading: false });
      }

      // Set up auth state change listener
      supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          set({ user: null, session: null });
          return;
        }

        if (session?.user) {
          try {
            // Fetch user data on auth state change with increased timeout
            const userData = await handleSupabaseError(
              supabase
                .from('users')
                .select('id, email, name, role, created_at, updated_at')
                .eq('id', session.user.id)
                .single(),
              { timeout: 360000, retries: 5 }
            );

            set({
              user: {
                ...session.user,
                ...userData,
                role: userData?.role || 'readonly',
              },
              session,
            });
          } catch (error) {
            console.error('Error fetching user data:', error);
            set({
              user: {
                ...session.user,
                role: 'readonly',
              },
              session,
            });
          }
        } else {
          set({ user: null, session: null });
        }
      });
    } catch (error) {
      console.error('Error loading auth session:', error);
      set({ loading: false });
      // Clear any invalid session data
      await supabase.auth.signOut();
    }
  },

  updateUserRole: async (role: UserRole) => {
    const { user } = useAuthStore.getState();
    if (!user) return;

    try {
      const userData = await handleSupabaseError(
        supabase
          .from('users')
          .update({ role })
          .eq('id', user.id)
          .select('id, email, name, role, created_at, updated_at')
          .single(),
        { timeout: 360000, retries: 5 }
      );

      set((state) => ({
        user: state.user ? { ...state.user, ...userData } : null,
      }));
    } catch (error: any) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },
}));

// Initialize auth on store creation
useAuthStore.getState().initializeAuth();